import React, { useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { Accordion, Card } from 'react-bootstrap'

// Card-styled collapsible section: h5 title in the header, everything else
// as body content. Extracted from SelectAdPlatformForm.jsx's tuned
// SelectAdPlatformSection — same look (see styles/shared/_accordion-card.scss)
// but kept as its own `.accordion-card` class rather than the older
// `goals-card` pattern (Goals/index.jsx, EventStatistics.jsx), which is
// scoped under `#campaigns-container`/`.event-series-stats_event-list` with
// spacing already shipped there — reusing that name unscoped would either
// miss those ancestors or risk nudging that existing UI.
//
// Uncontrolled by default (tracks its own open state, like every other
// caller). Pass `isOpen`+`onToggle` to drive it externally — e.g.
// CampaignForm.jsx's "Text Assets" section auto-expands itself when a
// "Request headlines" click needs to scroll into a field that section owns.
export default function AccordionCard({
  title,
  wrapperClassName,
  className,
  defaultOpen = true,
  isOpen: isOpenProp,
  onToggle,
  children,
}) {
  const [isOpenState, setIsOpenState] = useState(defaultOpen)
  const isControlled = isOpenProp !== undefined
  const isOpen = isControlled ? isOpenProp : isOpenState

  const handleToggle = () => {
    const next = !isOpen
    if (isControlled) {
      onToggle?.(next)
    } else {
      setIsOpenState(next)
    }
  }

  return (
    <Accordion activeKey={isOpen ? '0' : null} className={wrapperClassName}>
      <Card className={cn('accordion-card', className)}>
        <Accordion.Toggle eventKey="0">
          <Card.Header
            as={Card.Header}
            className={cn('accordion-card_header', { open: isOpen })}
            onClick={handleToggle}
          >
            {/* Plain string title gets the standard h5 treatment; a node
                (e.g. an InfoBox trigger) is trusted to bring its own markup —
                InfoBox's tooltip opens on hover/focus, not click, so it
                doesn't fight this header's click-to-toggle. */}
            {typeof title === 'string' ? <h5 className="mb-0">{title}</h5> : title}
            <div className="align-self-center ml-auto">
              <div className={cn('accordion-arrow', { open: isOpen })} />
            </div>
          </Card.Header>
        </Accordion.Toggle>
        <Accordion.Collapse eventKey="0">
          <Card.Body>{children}</Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  )
}

AccordionCard.propTypes = {
  title: PropTypes.node.isRequired,
  wrapperClassName: PropTypes.string,
  className: PropTypes.string,
  defaultOpen: PropTypes.bool,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  children: PropTypes.node,
}
